import type { AgentActivityEventPayload } from '@/infra/sse/activity/payloads'
import type {
  BashToolInput,
  EditToolInput,
  GlobToolInput,
  GrepToolInput,
  ReadToolInput,
  TaskToolInput,
  WebFetchToolInput,
  WebSearchToolInput,
  WriteToolInput,
} from '@/index'
import { type AgentMode, agentModeFrom } from '@/features/events/shared/types/enums'
import {
  calculateDiffStatsFromStrings,
  formatDiffStats,
  formatMcpToolForDisplay,
  getFileNameFromPath,
  truncateAtWordBoundary,
} from '../../shared/utils'
import type {
  AgentActivityStatus,
  FormattedAgentActivity,
  FormattedUserActivity,
  UserActivityStatus,
} from './types'

const ACTIVITY_CONTEXT_MAX_LENGTH = 60

export function formatAgentActivityEvent(
  event: AgentActivityEventPayload,
): FormattedAgentActivity | FormattedUserActivity | null {
  const { toolName, toolInput, eventType } = event
  const status = deriveStatus(eventType)
  const agentMode = agentModeFrom(event.eventMetadata.agentMode)

  if (status === 'system') {
    return formatSystemActivity(eventType, status, agentMode)
  }

  if (status === 'user') {
    return formatUserActivityFromEvent(eventType, agentMode)
  }

  if (!toolName) return null

  switch (toolName) {
    case 'Bash':
      return formatBashActivity(toolInput as unknown as BashToolInput, status, agentMode)

    case 'Edit':
      return formatEditActivity(toolInput as unknown as EditToolInput, status, agentMode)

    case 'Write':
      return formatWriteActivity(toolInput as unknown as WriteToolInput, status, agentMode)

    case 'Read':
      return formatReadActivity(toolInput as unknown as ReadToolInput, status, agentMode)

    case 'Glob':
      return formatGlobActivity(toolInput as unknown as GlobToolInput, status, agentMode)

    case 'Grep':
      return formatGrepActivity(toolInput as unknown as GrepToolInput, status, agentMode)

    case 'WebFetch':
      return formatWebFetchActivity(toolInput as unknown as WebFetchToolInput, status, agentMode)

    case 'WebSearch':
      return formatWebSearchActivity(toolInput as unknown as WebSearchToolInput, status, agentMode)

    case 'Task':
      return formatTaskActivity(toolInput as unknown as TaskToolInput, status, agentMode)

    case 'ExitPlanMode':
      return formatPlanModeActivity(
        status === 'post_tool' ? 'Agent exited plan mode' : 'Agent exiting plan mode',
        status,
        agentMode,
      )

    case 'EnterPlanMode':
      return formatPlanModeActivity(
        status === 'post_tool' ? 'Agent entered plan mode' : 'Agent entering plan mode',
        status,
        agentMode,
      )

    default: {
      const mcpFormatted = formatMcpToolForDisplay(toolName)
      if (mcpFormatted) {
        return {
          toolName: mcpFormatted.toolDisplayName,
          activityContext: `${mcpFormatted.toolDisplayName} ${mcpFormatted.actionDisplayName}`,
          status,
          agentMode,
        }
      }

      return {
        toolName,
        activityContext: '',
        status,
        agentMode,
      }
    }
  }
}

function deriveStatus(eventType: string | undefined): AgentActivityStatus | UserActivityStatus {
  if (!eventType) return 'idle'

  const normalized = eventType.trim().toUpperCase()

  if (normalized === 'PRE_TOOL_USE_ACTIVITY') return 'pre_tool'
  if (normalized === 'POST_TOOL_USE_ACTIVITY') return 'post_tool'

  if (normalized === 'SESSION_START_ACTIVITY') return 'system'
  if (normalized === 'SESSION_END_ACTIVITY') return 'system'
  if (normalized === 'SUBAGENT_STOP_ACTIVITY') return 'system'
  if (normalized === 'PRE_COMPACT_ACTIVITY') return 'system'
  if (normalized === 'IDLE_AGENT_ACTIVITY') return 'system'

  if (normalized === 'USER_INTERRUPT_ACTIVITY') return 'user'
  if (normalized === 'USER_RESUME_ACTIVITY') return 'user'
  if (normalized === 'USER_QUESTIONNAIRE_ANSWER_ACTIVITY') return 'user'

  return 'idle'
}

function formatBashActivity(
  input: BashToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  const context = input.description || input.command || 'command'
  return {
    toolName: 'Bash',
    activityContext: truncateAtWordBoundary(context, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatEditActivity(
  input: EditToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  const fileName = getFileNameFromPath(input.file_path)
  const stats = calculateDiffStatsFromStrings(input.old_string, input.new_string)
  const suffix = formatDiffStats(stats)

  return {
    toolName: 'Edit',
    activityContext: fileName,
    status,
    agentMode,
    suffix: suffix || undefined,
  }
}

function formatWriteActivity(
  input: WriteToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  const fileName = getFileNameFromPath(input.file_path)
  const lineCount = input.content.split('\n').length

  return {
    toolName: 'Write',
    activityContext: fileName,
    status,
    agentMode,
    suffix: `+${lineCount}`,
  }
}

function formatReadActivity(
  input: ReadToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  const fileName = getFileNameFromPath(input.file_path)

  return {
    toolName: 'Read',
    activityContext: fileName,
    status,
    agentMode,
  }
}

function formatGlobActivity(
  input: GlobToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: 'Glob',
    activityContext: truncateAtWordBoundary(input.pattern, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatGrepActivity(
  input: GrepToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: 'Grep',
    activityContext: truncateAtWordBoundary(input.pattern, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatWebFetchActivity(
  input: WebFetchToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: 'WebFetch',
    activityContext: truncateAtWordBoundary(input.url, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatWebSearchActivity(
  input: WebSearchToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: 'WebSearch',
    activityContext: truncateAtWordBoundary(input.query, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatTaskActivity(
  input: TaskToolInput,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: 'Task',
    activityContext: truncateAtWordBoundary(input.description, ACTIVITY_CONTEXT_MAX_LENGTH),
    status,
    agentMode,
  }
}

function formatPlanModeActivity(
  context: string,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  return {
    toolName: '',
    activityContext: context,
    status,
    agentMode,
  }
}

const SYSTEM_EVENT_MESSAGES: Record<string, string> = {
  SESSION_START_ACTIVITY: 'Session started',
  SESSION_END_ACTIVITY: 'Session ended',
  SUBAGENT_STOP_ACTIVITY: 'Sub agent completed all tasks',
  PRE_COMPACT_ACTIVITY: 'Compacting conversation',
  IDLE_AGENT_ACTIVITY: 'Claude is waiting for your input',
}

const USER_EVENT_MESSAGES: Record<string, string> = {
  USER_INTERRUPT_ACTIVITY: 'Claude Code interrupted',
  USER_RESUME_ACTIVITY: 'Claude Code resumed',
  USER_QUESTIONNAIRE_ANSWER_ACTIVITY: 'Answers submitted',
}

function formatSystemActivity(
  eventType: string | undefined,
  status: AgentActivityStatus,
  agentMode: AgentMode,
): FormattedAgentActivity {
  const normalized = eventType?.trim().toUpperCase() || ''

  return {
    toolName: '',
    activityContext: SYSTEM_EVENT_MESSAGES[normalized] || 'System activity',
    status,
    agentMode,
  }
}

function formatUserActivityFromEvent(
  eventType: string | undefined,
  agentMode: AgentMode,
): FormattedUserActivity {
  const normalized = eventType?.trim().toUpperCase() || ''

  return {
    toolName: '',
    activityContext: USER_EVENT_MESSAGES[normalized] || 'User action',
    status: 'user',
    agentMode,
  }
}
